import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import { forwardRef } from 'react'
import { RiCloseLine } from '@/shared/icons/remix'
import type { ToastSeverity } from './ToastProvider'
import {
  ToastErrorIcon,
  ToastInfoIcon,
  ToastSuccessIcon,
  ToastWarningIcon,
} from './toastIcons'
import { getAppToastSx, toastSeverityPalette } from './toastStyles'

const TOAST_ICON_BY_SEVERITY: Record<
  ToastSeverity,
  typeof ToastErrorIcon
> = {
  error: ToastErrorIcon,
  success: ToastSuccessIcon,
  info: ToastInfoIcon,
  warning: ToastWarningIcon,
}

interface AppToastProps {
  readonly message: string
  readonly severity: ToastSeverity
  readonly onClose: () => void
}

/** Figma toast shell — no MUI Alert (avoids default severity icons). */
export const AppToast = forwardRef<HTMLDivElement, AppToastProps>(function AppToast(
  { message, severity, onClose },
  ref,
) {
  const Icon = TOAST_ICON_BY_SEVERITY[severity]
  const palette = toastSeverityPalette[severity]

  return (
    <Box
      ref={ref}
      role="alert"
      data-testid="app-toast"
      data-severity={severity}
      sx={getAppToastSx(severity)}
    >
      <Box
        component="span"
        data-testid="app-toast-icon"
        sx={{
          display: 'inline-flex',
          flexShrink: 0,
          color: palette.icon,
          lineHeight: 0,
        }}
      >
        <Icon />
      </Box>
      <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
        {message}
      </Box>
      <IconButton
        type="button"
        onClick={onClose}
        aria-label="Close"
        size="small"
        data-testid="app-toast-close"
        sx={{
          flexShrink: 0,
          color: '#64748b',
          p: 0.25,
          ml: 0.5,
        }}
      >
        <RiCloseLine size={20} aria-hidden />
      </IconButton>
    </Box>
  )
})
